import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchUserLeaves, 
  addLeaveRequest, 
  clearErrors
} from '../../store/slices/leaveManagementSlice';
import CustomDateRangePicker from './components/DateRangePicker';
import LeaveDetailModal from './components/LeaveDetailModal';
import EditLeaveRemarksModal from './components/EditLeaveRemarksModal';
import { addDays } from 'date-fns';
import { sendLeaveRequestNotification } from '../../emailjs/emailService';
import './LeaveManagement.scss';

const LeaveManagement = () => {
  const dispatch = useDispatch();
  const { 
    leaves, 
    loading, 
    error: loadingError, 
    addingLeave: submitting, 
    addLeaveError: submitError,
    userLeaveBalance: leaveBalance
  } = useSelector(state => state.leaveManagement);
  const { user, employees } = useSelector(state => state.auth);
  
  // State for selected leave and modals
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // State for leave form
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: addDays(new Date(), 1),
    key: 'selection'
  });
  const [leaveDays, setLeaveDays] = useState(1);
  const [leaveType, setLeaveType] = useState('Vacation');
  const [remarks, setRemarks] = useState('');
  const [formErrors, setFormErrors] = useState({});
  
  // State for leave history filter
  const [statusFilter, setStatusFilter] = useState('All');
  
  // Fetch leaves on component mount
  useEffect(() => {
    let empID = user?.empID;
    
    // If empID not in user object, try to find it from employees array
    if (!empID && user?.email && employees.length > 0) {
      const currentEmployee = employees.find(emp => emp.email === user.email);
      empID = currentEmployee?.empID;
    }
    
    if (empID) {
      dispatch(fetchUserLeaves(empID));
    }
    
    return () => {
      dispatch(clearErrors());
    };
  }, [dispatch, user, employees]);
  
  // Calculate business days between two dates (excluding weekends)
  const calculateBusinessDays = (startDate, endDate) => {
    let count = 0;
    const curDate = new Date(startDate.getTime());
    
    while (curDate <= endDate) {
      const dayOfWeek = curDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        count++;
      }
      curDate.setDate(curDate.getDate() + 1);
    }
    
    return count;
  };
  
  // Update leave days when date range changes
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      const days = calculateBusinessDays(dateRange.startDate, dateRange.endDate);
      setLeaveDays(days);
    }
  }, [dateRange]);
  
  // Handle date range changes
  const handleDateRangeChange = (ranges) => {
    if (ranges.selection) {
      setDateRange(ranges.selection);
      setFormErrors(prev => ({...prev, dates: null}));
    }
  };
  
  // Handle leave type changes
  const handleLeaveTypeChange = (e) => {
    setLeaveType(e.target.value);
  };
  
  // Handle remarks changes
  const handleRemarksChange = (e) => {
    setRemarks(e.target.value);
  };
  
  // Handle leave request submission
  const handleSubmitLeave = (e) => {
    e.preventDefault();
    
    console.log('Form submitted - starting validation');
    console.log('Full user object:', user);
    console.log('Current form data:', {
      userEmpID: user?.empID,
      userEmail: user?.email,
      userDisplayName: user?.displayName,
      dateRange,
      leaveDays,
      leaveType,
      remarks,
      leaveBalance
    });
    
    // Get employee data - either from user object or employees array
    let currentEmployee = null;
    let empID = user?.empID;
    
    if (!empID && user?.email && employees.length > 0) {
      // Try to find employee data from employees array using email
      currentEmployee = employees.find(emp => emp.email === user.email);
      empID = currentEmployee?.empID;
      console.log('Found employee data from employees array:', currentEmployee);
    }
    
    console.log('Final empID to use:', empID);
    
    // Validate form
    const errors = {};
    
    if (!empID) {
      errors.empID = 'Employee ID not found. Please try logging in again.';
      console.log('Validation failed - no empID found');
    }
    
    if (!dateRange.startDate || !dateRange.endDate) {
      errors.dates = 'Please select leave dates';
    }
    
    if (leaveDays < 1) {
      errors.dates = 'Leave must be at least 1 day';
    }
    
    // Check leave balance
    if (leaveType === 'Vacation' && (leaveBalance?.vacation || leaveBalance?.annual || 0) < leaveDays) {
      errors.balance = 'Not enough vacation leave balance';
    } else if (leaveType === 'Sick' && (leaveBalance?.sick || 0) < leaveDays) {
      errors.balance = 'Not enough sick leave balance';
    }
    
    if (Object.keys(errors).length > 0) {
      console.log('Validation errors found:', errors);
      setFormErrors(errors);
      return;
    }
    
    console.log('Validation passed - submitting leave request');
    
    // Submit leave request  
    const finalEmployeeName = currentEmployee?.name || user.displayName || user.email;
    
    dispatch(addLeaveRequest({
      empID: empID,
      empEmail: user.email,
      employeeName: finalEmployeeName,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      leaveDays,
      leaveType,
      leaveRemarks: remarks,
      status: 'Pending'
    }))
      .unwrap()
      .then((result) => {
        // Show success message
        alert("Leave submitted successfully!");
        
        // Send email notification
        if (result) {
          const leaveData = {
            empID: empID,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            leaveDays,
            leaveType,
            leaveRemarks: remarks,
            status: 'Pending'
          };
          
          const userData = {
            name: finalEmployeeName,
            email: user.email,
            empID: empID
          };
          
          console.log('📧 Attempting to send leave request notification...');
          sendLeaveRequestNotification(leaveData, userData)
            .then((emailResult) => {
              if (emailResult.success) {
                console.log('✅ Leave request email sent successfully');
              } else {
                console.error('❌ Failed to send leave request email:', emailResult.error);
              }
            })
            .catch(error => console.error('❌ Error sending leave request email:', error));
        }
        
        // Reset form
        setDateRange({
          startDate: new Date(),
          endDate: addDays(new Date(), 1),
          key: 'selection'
        });
        setLeaveType('Vacation');
        setRemarks('');
        setFormErrors({});
        
        // Refresh the leaves list
        if (empID) {
          dispatch(fetchUserLeaves(empID));
        }
      })
      .catch((err) => {
        console.error('Failed to submit leave request:', err);
        alert('Failed to submit leave request. Please try again.');
      });
  };
  
  // View leave details
  const handleViewLeave = (leave) => {
    setSelectedLeave(leave);
    setShowDetailModal(true);
  };
  
  // Edit leave remarks
  const handleEditRemarks = (leave) => {
    setSelectedLeave(leave);
    setShowDetailModal(false);
    setShowEditModal(true);
  };
  
  // Close modals
  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedLeave(null);
  };
  
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedLeave(null);
  };
  
  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toLocaleDateString();
  };
  
  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Approved':
        return 'status-badge approved';
      case 'Rejected':
        return 'status-badge rejected';
      case 'Pending':
        return 'status-badge pending';
      case 'Cancelled':
        return 'status-badge cancelled';
      default:
        return 'status-badge';
    }
  };
  
  // Get display leave type
  const getDisplayLeaveType = (type) => {
    if (type === 'Vacation') return 'Vacation';
    if (type === 'Sick') return 'Sick Leave';
    return type;
  };
  
  // Filter leaves based on status
  const filteredLeaves = statusFilter === 'All' 
    ? leaves 
    : leaves.filter(leave => leave.status === statusFilter);
  
  // Handle status filter change
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
  };
  
  // Available status options for filter
  const statusOptions = ['All', 'Pending', 'Approved', 'Rejected', 'Cancelled'];
  
  return (
    <div className="leave-management-container">
      <div className="page-header">
        <h1>Leave Management</h1>
        <p>Apply for leave and track your leave requests</p>
      </div>
      
      <div className="leave-summary">
        <h3>Your Leave Balance</h3>
        <div className="balance-cards">
          <div className="balance-card">
            <div className="balance-label">Vacation</div>
            <div className="balance-value vacation">{leaveBalance?.vacation || leaveBalance?.annual || 0}</div>
          </div>
          <div className="balance-card">
            <div className="balance-label">Sick Leave</div>
            <div className="balance-value sick">{leaveBalance?.sick || 0}</div>
          </div>
        </div>
      </div>
      
      <div className="content-section">
        <div className="leave-request-form">
          <h3>Apply for Leave</h3>
          <form onSubmit={handleSubmitLeave}>
            <div className="form-group">
              <label>Leave Period</label>
              <div className="date-range-wrapper">
                <CustomDateRangePicker
                  ranges={[dateRange]}
                  onChange={handleDateRangeChange}
                />
              </div>
              
              <div className="leave-days-info">
                <span className="days-label">Business Days:</span>
                <span className="days-value">{leaveDays}</span>
              </div>
              
              {formErrors.dates && (
                <div className="error-message">{formErrors.dates}</div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="leaveType">Leave Type</label>
              <select
                id="leaveType"
                value={leaveType}
                onChange={handleLeaveTypeChange}
              >
                <option value="Vacation">Vacation</option>
                <option value="Sick">Sick Leave</option>
              </select>
              
              {formErrors.balance && (
                <div className="error-message">{formErrors.balance}</div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="leaveRemarks">Remarks (Optional)</label>
              <textarea
                id="leaveRemarks"
                value={remarks}
                onChange={handleRemarksChange}
                placeholder="Enter any additional information about your leave request"
              ></textarea>
            </div>
            
            <button 
              type="submit" 
              className="submit-btn"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : 'Submit Leave Request'}
            </button>
            
            {submitError && (
              <div className="submit-error">
                Failed to submit request: {submitError}
              </div>
            )}
          </form>
        </div>
        
        <div className="leave-history">
          <div className="leave-history-header">
            <h3>Your Leave History</h3>
            
            {/* Status Filter */}
            <div className="status-filter">
              <span className="filter-label">Filter by Status:</span>
              <div className="filter-buttons">
                {statusOptions.map(status => (
                  <button
                    key={status}
                    className={`filter-btn ${statusFilter === status ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange(status)}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          ) : loadingError ? (
            <div className="load-error">
              Failed to load leave history: {loadingError}
            </div>
          ) : filteredLeaves.length === 0 ? (
            <div className="empty-leaves">
              <p>
                {statusFilter === 'All' 
                  ? 'You have not applied for any leave yet.' 
                  : `No ${statusFilter.toLowerCase()} leaves found.`}
              </p>
            </div>
          ) : (
            <table className="leave-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeaves.map(leave => (
                  <tr 
                    key={leave.id} 
                    className={leave.status === 'Rejected' ? 'rejected' : ''}
                  >
                    <td>{getDisplayLeaveType(leave.leaveType)}</td>
                    <td>{formatDate(leave.startDate)}</td>
                    <td>{formatDate(leave.endDate)}</td>
                    <td>{leave.leaveDays}</td>
                    <td>
                      <span className={getStatusBadgeClass(leave.status)}>
                        {leave.status}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        <button 
                          onClick={() => handleViewLeave(leave)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      
      {showDetailModal && selectedLeave && (
        <LeaveDetailModal
          leave={selectedLeave}
          onClose={handleCloseDetailModal}
          onEdit={handleEditRemarks}
        />
      )}
      
      {showEditModal && selectedLeave && (
        <EditLeaveRemarksModal
          leave={selectedLeave}
          onClose={handleCloseEditModal}
        />
      )}
    </div>
  );
};

export default LeaveManagement; 